/**
 * Font Configuration Types
 *
 * Type definitions for project-level platform-aware font mappings.
 * Stores per-schema-font, per-platform mappings in Firestore.
 *
 * @module font-config/types
 */

// ============================================================================
// Platform Types
// ============================================================================

/**
 * Supported platform identifiers for font mapping.
 * Fonts are tied to the OS, not the browser.
 * TikTok/FB/IG WebView on iOS = same fonts as Safari.
 */
export type PlatformId = 'ios' | 'macos' | 'android' | 'windows' | 'linux';

/**
 * Font category for generic CSS fallback
 */
export type FontCategory = 'sans-serif' | 'serif' | 'monospace';

// ============================================================================
// System Font Catalog Types
// ============================================================================

/**
 * A single system font entry in the catalog
 */
export interface SystemFontEntry {
  name: string;
  weights: number[];
}

/**
 * Platform font catalog grouped by category
 */
export interface PlatformFontCatalog {
  'sans-serif': SystemFontEntry[];
  'serif': SystemFontEntry[];
  'monospace': SystemFontEntry[];
}

/**
 * Complete system font catalog across all platforms
 */
export type SystemFontCatalog = Record<PlatformId, PlatformFontCatalog>;

// ============================================================================
// Project Font Config (Firestore)
// ============================================================================

/**
 * Project-level font configuration stored in Firestore.
 * Path: projects/{projectId}/settings/fontConfig
 *
 * Only user-overridden values are stored. Defaults fill the gaps.
 */
export interface ProjectFontConfig {
  version: 1;

  /**
   * Per-schema-font, per-platform mappings.
   * Key L1 = font slot key (e.g. "SF Pro", "Avenir Next", "Georgia")
   * Key L2 = PlatformId
   * Value = system font name from the catalog
   *
   * Missing L1 key = use DEFAULT_PLATFORM_MAPPINGS for that font.
   * Missing L2 key = use DEFAULT_PLATFORM_MAPPINGS for that platform.
   */
  platformMappings: Record<string, Partial<Record<PlatformId, string>>>;

  /**
   * User-renamable display labels for font slots.
   * Key = font slot key (e.g. "SF Pro"), Value = display label (e.g. "Main Font").
   * Only overridden labels stored. Missing key = default label = font name.
   */
  fontLabels?: Record<string, string>;

  /**
   * Project-uploaded custom fonts, keyed by server-generated fontId.
   * Written ONLY by builder-service (ingestion endpoint) via dot-notation
   * update; the panel reads them and maps slots to `upload:<fontId>`.
   */
  customFonts?: Record<string, CustomFontDefinition>;

  updatedAt: string;
}

/** License/audit record for an uploaded font (copyright is the real risk). */
export interface CustomFontLicense {
  type: 'attested';
  /** Firebase uid of the member who confirmed the license attestation */
  attestedBy: string;
  /** ISO timestamp of the attestation */
  attestedAt: string;
  note?: string;
  /** OS/2 fsType had restricted-embedding bits — soft warning only (bits are unreliable) */
  fsTypeWarning?: boolean;
}

/**
 * A project-uploaded custom font — WebFontAsset-compatible so the shared
 * loader/bake/SSR chain consumes it without caring about source.
 * Produced exclusively by the builder-service ingestion pipeline
 * (sanitize-by-recompile → WOFF2 → GCS), never written by the client.
 */
export interface CustomFontDefinition {
  /** 'upload:<fontId>' — the value stored in platformMappings */
  id: string;
  /** UI-only display name (escaped when rendered) */
  displayName: string;
  /**
   * Server-sanitized CSS family name (allowlist [A-Za-z0-9 _-]{1,64}) —
   * the ONLY user-derived string that reaches @font-face / chains.
   */
  cssFamily: string;
  source: 'upload';
  category: FontCategory;
  weights: number[];
  primaryWeight: number;
  /** Absolute GCS URLs (project fonts live in one env's bucket — no {env} placeholder) */
  faces: WebFontFace[];
  fallbackMetrics: FallbackMetrics;
  license: CustomFontLicense;
  /** Original upload filename — audit metadata only, never used in paths/CSS */
  originalFilename?: string;
  /** sha256 of the sanitized WOFF2 (audit/dedup) */
  sha256?: string;
  createdAt: string;
}

// ============================================================================
// Web Font Types (self-hosted WOFF2 — curated Google catalog + uploads)
// ============================================================================

/**
 * Metric overrides for the synthetic "<family> Fallback" @font-face that
 * makes the swap from a local system font to the web font layout-stable.
 * All values are fractions of 1em (0.9688 → CSS "96.88%").
 *
 * Formula (matches fontaine / next-font): ascent ÷ (unitsPerEm × sizeAdjust).
 */
export interface FallbackMetrics {
  ascentOverride: number;
  descentOverride: number;
  lineGapOverride: number;
  sizeAdjust: number;
}

/**
 * One deliverable WOFF2 face of a web font.
 */
export interface WebFontFace {
  weight: number;
  style: 'normal' | 'italic';
  /**
   * WOFF2 URL. May contain the literal `{env}` placeholder — substitute
   * with resolveWebFontForEnv() before handing to a loader/bake.
   */
  woff2: string;
  /** sha384 SRI for the preload <link>. Empty string until the pipeline computes it. */
  sri: string;
  /**
   * CSS unicode-range for subset faces (latin / latin-ext / cyrillic each
   * get their own face + range). Absent = face covers everything it has.
   */
  unicodeRange?: string;
}

/**
 * Source-agnostic resolved web font — the single contract consumed by the
 * loader, the published-HTML bake, and SSR. Curated catalog entries and
 * (Phase 2) project-uploaded fonts both resolve to this shape.
 */
export interface WebFontAsset {
  /** 'google:<slug>' (curated) or 'upload:<fontId>' (project upload) */
  id: string;
  /** CSS font-family name emitted into @font-face and fallback chains */
  family: string;
  category: FontCategory;
  source: 'google' | 'upload';
  /** SPDX-ish license tag for curated fonts (e.g. "OFL-1.1") */
  license: string;
  weights: number[];
  /** The weight worth a preload <link> (body text weight) */
  primaryWeight: number;
  faces: WebFontFace[];
  fallbackMetrics: FallbackMetrics;
}

/**
 * Default empty font config (used when no config exists in Firestore)
 */
export const DEFAULT_FONT_CONFIG: ProjectFontConfig = {
  version: 1,
  platformMappings: {},
  updatedAt: '',
};
